import clsx from "clsx";
import { ArrowLeft, Check, FileText, FileUp, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { api } from "../../api/client";
import type { GraphicsImport, GraphicsImportItem, LineKind, ZoneKind } from "../../api/types";
import { designate } from "../../components/attention";
import { Callout } from "../../components/Callout";
import { Checkbox } from "../../components/Checkbox";
import { Dialog } from "../../components/Dialog";
import { Spinner } from "../../components/EmptyState";
import { DisclosureRow } from "../../components/panel";
import { Motion, Presence } from "../../components/presence";
import { Select } from "../../components/Select";
import { Badge, Button, IconButton, inputSmClass } from "../../components/ui";
import { useMediaQuery } from "../../components/useMediaQuery";
import { k, locale, t, tServer } from "../../i18n/i18n";
import { term } from "../../i18n/vocabulary";
import { lengthM, type LonLat } from "../../map/graphics/geometry";
import { useGraphics } from "../../map/graphics/useGraphics";
import { plural } from "../../plural";
import { fileSize } from "../received/ImportDialog";
import { Waiting } from "../../components/Waiting";

/** Import de graphiques tactiques (registre C11) : fichier ATAK (CoT, paquet de données), KMZ ou KML, NVG. Le
 *  serveur lit le fichier et propose ses zones, lignes et panaches NRBC, déjà contrôlés (emprise du camp,
 *  doublons) ; l'utilisateur coche, corrige le type d'un graphique non typé, puis les ajoute d'un bloc au
 *  dossier, avec l'annulation habituelle. Les unités du fichier ne s'importent pas. */

const ACCEPTED = ".kmz,.kml,.zip,.cot,.xml,.nvg,application/vnd.google-earth.kmz,application/xml";
const MAX_BYTES = 15 * 1024 * 1024;
const AGENTS: Record<string, string> = {
  chemical: k("chimique"),
  biological: k("biologique"),
  radiological: k("radiologique"),
  nuclear: k("nucléaire"),
};

function shapeOf(item: GraphicsImportItem): string {
  if (item.family === "operationalGraphics") {
    const zone = item.graphic;
    return zone.ring
      ? t("contour de {n} sommets", { n: zone.ring.length })
      : t("cercle de {radius} m", { radius: Math.round(zone.radiusMetres).toLocaleString(locale()) });
  }
  if (item.family === "lineGraphics") {
    const points: LonLat[] = item.graphic.coordinates.map((c) => [c.longitude, c.latitude]);
    return `${plural(points.length, t("point"), t("points"))}, ${(lengthM(points) / 1000).toLocaleString(locale(), { maximumFractionDigits: 1 })} km`;
  }
  const plume = item.graphic;
  const agent = AGENTS[plume.agent];
  return t("{agent}, vent {speed} nœuds vers {bearing}°", {
    agent: agent ? t(agent) : plume.agent,
    speed: plume.windSpeedKnots.toLocaleString(locale()),
    bearing: Math.round(plume.plumeBearingDegrees),
  });
}

export function GraphicsImportDialog({ exerciseId, onClose }: { exerciseId: string; onClose: () => void }) {
  const { catalog, importMany, busy: saving } = useGraphics();
  const [file, setFile] = useState<File | null>(null);
  const [over, setOver] = useState(false);
  // Sur tablette, rien ne se dépose : la zone se touche pour choisir le fichier.
  const coarse = useMediaQuery("(pointer: coarse)");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<GraphicsImport | null>(null);
  const [items, setItems] = useState<GraphicsImportItem[]>([]);
  const [chosen, setChosen] = useState<Set<number>>(new Set());
  const [showSkipped, setShowSkipped] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const pickRef = useRef<HTMLElement | null>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const tooBig = Boolean(file && file.size > MAX_BYTES);

  useEffect(() => {
    if (!result) return;
    setItems(result.items);
    setChosen(new Set(result.items.flatMap((item, i) => (item.selected ? [i] : []))));
  }, [result]);

  const read = async () => {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      setResult(await api.analyzeGraphicsImport(exerciseId, file));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const retype = (index: number, kind: string) =>
    setItems((all) =>
      all.map((item, i) =>
        i !== index || item.family === "cbrnPlumes"
          ? item
          : ({ ...item, graphic: { ...item.graphic, kind }, known: true } as GraphicsImportItem),
      ),
    );
  const toggle = (index: number) =>
    setChosen((c) => {
      const next = new Set(c);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  const selected = items.filter((_, i) => chosen.has(i));

  return (
    <Dialog
      title={t("Importer des graphiques tactiques")}
      subtitle={
        result
          ? t("Cochez les graphiques à ajouter au dossier ; précisez le type de ceux qui n'en ont pas.")
          : t("Zones, lignes et panaches NRBC d'un fichier ATAK, KMZ ou NVG, proposés avant tout ajout.")
      }
      width={result ? 640 : 560}
      onClose={onClose}
      footer={
        result ? (
          <>
            <Button variant="ghost" className="mr-auto" onClick={() => setResult(null)} disabled={saving}>
              <ArrowLeft size={14} /> {t("Autre fichier")}
            </Button>
            <Button variant="secondary" onClick={onClose}>
              {t("Annuler")}
            </Button>
            {/* Aucun graphique coché : le bouton désigne la liste à cocher (R12). */}
            <Button
              disabled={saving}
              blocked={selected.length === 0}
              onBlocked={() => designate(listRef.current, "ring")}
              onClick={() => importMany(selected, result.filename, onClose)}
            >
              {saving ? <Spinner decorative /> : <Check size={14} />}
              {selected.length > 1 ? t("Importer {n} graphiques", { n: selected.length }) : t("Importer le graphique")}
            </Button>
          </>
        ) : (
          <>
            <Button variant="secondary" onClick={onClose}>
              {t("Annuler")}
            </Button>
            {/* Pas de fichier, ou fichier trop lourd : le bouton désigne le choix du fichier (R12). */}
            <Button disabled={busy} blocked={!file || tooBig} onBlocked={() => designate(pickRef.current, "ring")} onClick={() => void read()}>
              {busy ? <Spinner decorative /> : <FileUp size={14} />}
              {busy ? <Waiting>{t("Lecture du fichier…")}</Waiting> : t("Lire le fichier")}
            </Button>
          </>
        )
      }
    >
      {!result ? (
        <div className="space-y-4 px-4 py-4">
          {file ? (
            <Motion
              key="file"
              origin="fade"
              ref={(node) => {
                pickRef.current = node;
              }}
              className="flex items-center gap-3 rounded-xl border border-line bg-canvas/50 px-3.5 py-3"
            >
              <FileText size={24} className="shrink-0 text-ink-soft" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-title font-medium text-ink">{file.name}</p>
                <p className={clsx("text-meta", tooBig ? "text-danger" : "text-ink-muted")}>
                  {fileSize(file.size)}
                  {tooBig && ` — ${t("trop volumineux : 15 Mo au plus")}`}
                </p>
              </div>
              <IconButton size="sm" onClick={() => setFile(null)} title={t("Choisir un autre fichier")} aria-label={t("Choisir un autre fichier")}>
                <X size={14} />
              </IconButton>
            </Motion>
          ) : (
            <button
              ref={(node) => {
                pickRef.current = node;
              }}
              type="button"
              onClick={() => input.current?.click()}
              onDragOver={(e) => {
                e.preventDefault();
                setOver(true);
              }}
              onDragLeave={() => setOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setOver(false);
                const given = e.dataTransfer.files[0];
                if (given) setFile(given);
              }}
              className={clsx(
                "flex w-full flex-col items-center gap-2 rounded-xl border-2 border-dashed px-6 py-8 text-center transition-colors",
                over ? "border-accent bg-accent-soft/60" : "border-line-strong hover:border-accent hover:bg-hover",
              )}
            >
              <FileUp size={24} className="text-ink-soft" />
              <span className="text-title font-medium text-ink">{coarse ? t("Touchez pour choisir le fichier de situation") : t("Déposez le fichier de situation, ou cliquez pour le choisir")}</span>
              <span className="text-meta leading-snug text-ink-muted">
                {t(
                  "ATAK : paquet de données (.zip) ou objet CoT (.cot, .xml) — KMZ ou KML — NVG 2.0 (.nvg, .xml) ; 15 Mo au plus. Le format se reconnaît au contenu.",
                )}
              </span>
            </button>
          )}
          <Presence show={Boolean(error)}>{error && <Callout tone="danger">{error}</Callout>}</Presence>
          <input
            ref={input}
            type="file"
            accept={ACCEPTED}
            className="hidden"
            onChange={(e) => {
              const picked = e.target.files?.[0];
              if (picked) setFile(picked);
            }}
          />
        </div>
      ) : (
        <Motion origin="fade">
          <p className="border-b border-line px-4 py-3 text-ui leading-relaxed text-ink-soft">
            <span className="font-medium text-ink">{result.filename}</span>{" "}
            {t("({format}) : {found}{skipped}{units}.", {
              format: tServer(result.format),
              found: items.length
                ? plural(items.length, t("graphique proposé"), t("graphiques proposés"))
                : t("aucun graphique à importer"),
              skipped: result.skipped.length > 0 ? `, ${plural(result.skipped.length, t("objet écarté"), t("objets écartés"))}` : "",
              units:
                result.units > 0
                  ? `, ${t("{units} (elles viennent de l'ordre de bataille du dossier)", {
                      units: plural(result.units, t("unité non importée"), t("unités non importées")),
                    })}`
                  : "",
            })}
          </p>
          {items.length > 0 && (
            <ul ref={listRef} className="divide-y divide-line">
              {items.map((item, i) => {
                const checked = chosen.has(i);
                const kinds =
                  item.family === "operationalGraphics"
                    ? catalog?.zones
                    : item.family === "lineGraphics"
                      ? catalog?.lines
                      : null;
                return (
                  <li key={i} className={clsx("flex items-start gap-3 px-4 py-2.5 transition-colors", checked && "bg-selected")}>
                    <Checkbox
                      checked={checked}
                      onChange={() => toggle(i)}
                      aria-label={item.graphic.label ?? tServer(item.source)}
                      className="mt-0.5"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-ui font-semibold text-ink">{item.graphic.label ?? tServer(item.source)}</span>
                        {item.duplicate && (
                          <Badge dot tone="neutral">
                            {t("Déjà dans le dossier")}
                          </Badge>
                        )}
                        {!item.known && (
                          <Badge dot tone="warn">
                            {t("Type à vérifier")}
                          </Badge>
                        )}
                      </div>
                      <p className="mt-0.5 text-meta text-ink-muted">
                        {item.source !== item.graphic.label && `${t("« {name} »", { name: tServer(item.source) })} · `}
                        {shapeOf(item)} · {tServer(item.detected)}
                      </p>
                    </div>
                    {kinds && (
                      <div className="w-[150px] shrink-0">
                        <Select
                          className={inputSmClass}
                          value={item.family === "cbrnPlumes" ? "" : (item.graphic.kind as ZoneKind | LineKind)}
                          onChange={(e) => retype(i, e.target.value)}
                          aria-label={t("Type du graphique")}
                        >
                          {kinds.map((kind) => (
                            <option key={kind.kind} value={kind.kind}>
                              {kind.short} — {term(kind.name)}
                            </option>
                          ))}
                        </Select>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
          {result.skipped.length > 0 && (
            <div className="border-t border-line px-2 py-2">
              <DisclosureRow
                title={t("Objets écartés")}
                count={result.skipped.length}
                open={showSkipped}
                onToggle={() => setShowSkipped((s) => !s)}
              >
                <ul className="space-y-1 px-2 pt-1 pb-1">
                  {result.skipped.map((s, i) => (
                    <li key={i} className="text-meta leading-snug text-ink-muted">
                      <span className="text-ink-soft">{tServer(s.name)}</span> — {tServer(s.reason)}
                    </li>
                  ))}
                </ul>
              </DisclosureRow>
            </div>
          )}
        </Motion>
      )}
    </Dialog>
  );
}
